import { LoaderCircle } from 'lucide-react';
import type * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * The states a screen is in when it has nothing to show yet, or nothing to show
 * at all.
 *
 * These were the least designed part of the product — a centred grey sentence
 * for loading, a small icon for empty, raw red text for failure — which is a
 * shame, because they are what a user sees on their very first visit and every
 * time something goes wrong. They are grouped in one file because they are one
 * idea: the shape of a screen with no content in it.
 */

/**
 * A placeholder that traces the content it is standing in for.
 *
 * The sweep is slow and low-contrast on purpose. A fast, bright shimmer draws
 * more attention than the data it is replacing, which inverts the point.
 */
function Skeleton({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="skeleton"
      aria-hidden
      className={cn(
        'relative overflow-hidden rounded-sm bg-placeholder',
        'after:absolute after:inset-0 after:animate-[skeleton-sweep_1.6s_ease-in-out_infinite]',
        'after:bg-gradient-to-r after:from-transparent after:via-[oklch(1_0_0_/_0.55)] after:to-transparent',
        'motion-reduce:after:hidden',
        className
      )}
      {...props}
    />
  );
}

/**
 * Announces a wait to a screen reader without putting anything on screen.
 *
 * `<output>` rather than a `<span role="status">`: it carries the live-region
 * semantics natively, so there is no ARIA role to get wrong.
 */
function LoadingAnnouncement({ children }: { children: React.ReactNode }) {
  return <output className="sr-only">{children}</output>;
}

function Spinner({ className, ...props }: React.ComponentProps<'svg'>) {
  return (
    <LoaderCircle
      data-slot="spinner"
      aria-hidden
      className={cn('size-4 animate-spin text-foreground-faint', className)}
      {...props}
    />
  );
}

/**
 * Nothing here — and, where it helps, a way out of that.
 *
 * `compact` is for the inside of a board column, where the surrounding frame
 * already explains what is missing and a full illustration-sized block would
 * push the column's own scroll around.
 */
function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  compact = false,
  className,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  action?: React.ReactNode;
  compact?: boolean;
  className?: string;
}) {
  return (
    <div
      data-slot="empty-state"
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compact ? 'gap-2 px-4 py-10' : 'gap-3 px-6 py-16',
        className
      )}
    >
      {Icon && (
        <span
          className={cn(
            'flex items-center justify-center rounded-full border border-border-subtle bg-surface-muted text-foreground-disabled',
            compact ? 'size-9' : 'size-12'
          )}
        >
          <Icon className={compact ? 'size-4' : 'size-5'} />
        </span>
      )}
      <div className="space-y-1">
        <p
          className={cn(
            compact ? 'type-caption text-foreground-muted' : 'type-subhead text-foreground'
          )}
        >
          {title}
        </p>
        {description && (
          <p className="type-caption mx-auto max-w-xs text-balance text-foreground-muted">
            {description}
          </p>
        )}
      </div>
      {action && <div className="pt-1">{action}</div>}
    </div>
  );
}

export { EmptyState, LoadingAnnouncement, Skeleton, Spinner };
